import { useEffect, useState } from "react";
import client from "@/lib/api";
import { SectionHeader, notifyError, notifyOk } from "./_ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function TranslationSettings() {
  const [tr, setTr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    client
      .get("/admin/settings")
      .then((r) => setTr(r.data.translation || { enabled: false, provider: "deepl", api_key: "", api_url: "", translate_comment: true }))
      .catch(notifyError);
  }, []);

  if (!tr) return <div className="text-sm text-neutral-500">Загрузка…</div>;

  const set = (k, v) => setTr({ ...tr, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/admin/settings", { translation: tr });
      notifyOk("Сохранено");
    } catch (e) { notifyError(e); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await client.put("/admin/settings", { translation: tr });
      const { data } = await client.post("/admin/translation/test", {
        api_key: tr.api_key || "",
        api_url: tr.api_url || "",
      });
      setTestResult(data);
      if (data.ok) notifyOk("Подключение работает");
      else notifyError({ message: data.error || "Ошибка" });
    } catch (e) { notifyError(e); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Перевод (DeepL)"
        description="Перевод комментария о состоянии автомобиля в русскую копию акта. Перевод выполняется один раз при создании акта и сохраняется — повторные скачивания PDF лимит не расходуют."
      />

      <div className="border border-neutral-200 rounded-lg p-4 space-y-4 max-w-2xl">
        <div className="flex items-center gap-3">
          <Switch
            checked={!!tr.enabled}
            onCheckedChange={(v) => set("enabled", v)}
            data-testid="translation-enabled"
          />
          <span className="text-sm font-medium">Использовать автоперевод</span>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-neutral-500">
            API-ключ DeepL (бесплатные ключи оканчиваются на «:fx»)
          </Label>
          <Input
            value={tr.api_key || ""}
            onChange={(e) => set("api_key", e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx:fx"
            className="border-neutral-300 font-mono text-sm"
            data-testid="translation-key"
          />
          <p className="text-xs text-neutral-500">
            Адрес API определяется автоматически по типу ключа. Бесплатный тариф — 500 000 символов в месяц.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-neutral-500">
            Адрес API (необязательно, оставьте пустым для автоопределения)
          </Label>
          <Input
            value={tr.api_url || ""}
            onChange={(e) => set("api_url", e.target.value)}
            placeholder="https://api-free.deepl.com/v2/translate"
            className="border-neutral-300 text-sm"
          />
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={tr.translate_comment !== false}
            onCheckedChange={(v) => set("translate_comment", v)}
            data-testid="translation-comment"
          />
          <span className="text-sm">Переводить комментарий о состоянии автомобиля</span>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button onClick={save} disabled={saving} data-testid="translation-save">
            {saving ? "Сохранение…" : "Сохранить"}
          </Button>
          <Button variant="outline" onClick={runTest} disabled={testing} data-testid="translation-test">
            {testing ? "Проверка…" : "Проверить подключение"}
          </Button>
        </div>

        {testResult && (
          <div
            className={`text-sm rounded-md p-3 ${
              testResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
            }`}
            data-testid="translation-test-result"
          >
            {testResult.ok ? (
              <>Работает. Пример перевода: «{testResult.sample}»</>
            ) : (
              <>Ошибка: {testResult.error}</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
